import { useMemo } from 'react';

const useUserSteps = (redux, nextStep) => {
  const steps = redux.profile.steps || [];

  const isStepDone = (stepId) => {
    const step = steps.find((stp) => stp.id === stepId);
    return step && step.done;
  };

  const isStepPending = (stepId) => {
    const step = steps.find((stp) => stp.id === stepId);
    return step && !step.done;
  };

  const isStepExists = (stepId) => {
    return steps.some((stp) => stp.id === stepId);
  };

  // Check if the user got the joinWorkspace step
  const oldUser = useMemo(() => !isStepExists('joinWorkspace'), [steps]);

  // Steps status
  const isEmailValidationDone = useMemo(() => isStepDone('emailValidation'), [steps]);
  const isJoinWorkspaceDone = useMemo(() => isStepDone('joinWorkspace'), [steps]);
  const isPluginInstallationDone = useMemo(() => isStepDone('pluginInstallation'), [steps]);
  const isLinkedinValidationDone = useMemo(() => isStepDone('linkedinValidation'), [steps]);

  // Specific cases
  const canAccessMainApp = useMemo(() => {
    return (
      nextStep === -1 ||
      (isEmailValidationDone &&
        isJoinWorkspaceDone &&
        isPluginInstallationDone &&
        isLinkedinValidationDone &&
        !oldUser) ||
      (isEmailValidationDone && oldUser && isPluginInstallationDone && isLinkedinValidationDone)
    );
  }, [
    nextStep,
    isEmailValidationDone,
    isJoinWorkspaceDone,
    isPluginInstallationDone,
    isLinkedinValidationDone,
    oldUser,
  ]);

  const showEmailValidation = useMemo(() => {
    return isStepExists('emailValidation') && !isEmailValidationDone;
  }, [isEmailValidationDone]);

  const showJoinWorkspace = useMemo(() => {
    return isEmailValidationDone && isStepExists('joinWorkspace') && !isJoinWorkspaceDone;
  }, [isEmailValidationDone, isJoinWorkspaceDone]);

  const needsOnboarding = useMemo(() => {
    if (!isEmailValidationDone) return false;

    if (!oldUser && isJoinWorkspaceDone && !isPluginInstallationDone) {
      return true;
    }
    if (!oldUser && isJoinWorkspaceDone && !isLinkedinValidationDone) {
      return true;
    }
    if (oldUser && !isPluginInstallationDone) {
      return true;
    }
    if (oldUser && !isLinkedinValidationDone) {
      return true;
    }

    return false;
  }, [
    isEmailValidationDone,
    oldUser,
    isJoinWorkspaceDone,
    isPluginInstallationDone,
    isLinkedinValidationDone,
  ]);

  const redirectTo = useMemo(() => {
    if (showEmailValidation) return '/emailvalidation';
    if (showJoinWorkspace) return '/joinWorkspace';
    if (needsOnboarding) return '/welcome';
    return null;
  }, [showEmailValidation, showJoinWorkspace, needsOnboarding]);

  const showLegacyPopup = useMemo(() => {
    const inBillingPage = window.location.pathname.includes('billing');
    const hasOrganizationCredits = redux.credits.organizationCredits;
    const shouldShowPopUp = redux.credits.showPopUp;

    return (
      (nextStep === -1 || canAccessMainApp) &&
      hasOrganizationCredits &&
      !inBillingPage &&
      shouldShowPopUp
    );
  }, [nextStep, canAccessMainApp, redux.credits]);

  return {
    oldUser,
    canAccessMainApp,
    showEmailValidation,
    showJoinWorkspace,
    needsOnboarding,
    redirectTo,
    showLegacyPopup,
    isPluginInstallationDone,
    isLinkedinValidationDone,
    isEmailValidationDone,
    isJoinWorkspaceDone,
  };
};

export default useUserSteps;
